/**
 * Made by: Growly Oy
 * Product Card Swatch Availability
 * Marks out-of-stock variants in product card swatches
 *
 * Version: 3.0 - LOCAL DATA (No API calls)
 *
 * Changes in 3.0:
 * - Removed all API calls - reads inventory from DOM JSON
 * - Uses .product-card-variants-data JSON rendered by Liquid
 * - Much faster - no network requests needed
 *
 * Changes in 2.4:
 * - Added bfcache support - resets state when page restored from back-forward cache
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    debounceDelay: 50,  // ms to wait before processing
  };

  let mainObserver = null;
  let observedCards = new WeakSet();
  let processedCards = new WeakSet();
  let resizeObserver = null;

  // Queue for debounced processing
  let pendingCards = [];
  let debounceTimer = null;

  /**
   * Wait for CustomInventoryAPI to be available (for getStockStatus function)
   */
  function waitForInventoryAPI(maxAttempts = 50, interval = 100) {
    return new Promise((resolve) => {
      let attempts = 0;
      const check = () => {
        if (window.CustomInventoryAPI) {
          resolve(true);
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(check, interval);
        } else {
          resolve(false);
        }
      };
      check();
    });
  }

  /**
   * Get inventory data from DOM JSON
   * @param {HTMLElement} productCard
   * @returns {Object} - { variantId: { ownStock, externalStock, ... }, ... }
   */
  function getInventoryDataFromDOM(productCard) {
    const variantsDataEl = productCard.querySelector('.product-card-variants-data');
    if (!variantsDataEl) return {};

    try {
      const data = JSON.parse(variantsDataEl.textContent);
      // Transform to inventory format: { variantId: { ownStock, externalStock } }
      const inventoryData = {};
      for (const [variantId, variantInfo] of Object.entries(data)) {
        inventoryData[variantId] = {
          ownStock: variantInfo.ownStock || 0,
          externalStock: variantInfo.externalStock || 0
        };
      }
      return inventoryData;
    } catch (err) {
      return {};
    }
  }

  /**
   * Get variants metadata from DOM JSON
   * @param {HTMLElement} productCard
   * @returns {Object}
   */
  function getVariantsData(productCard) {
    const variantsDataEl = productCard.querySelector('.product-card-variants-data');
    if (!variantsDataEl) return {};

    try {
      return JSON.parse(variantsDataEl.textContent);
    } catch (err) {
      return {};
    }
  }

  /**
   * Select first available swatch and dispatch change event
   * @param {HTMLElement} productCard
   * @param {NodeList} swatchInputs
   * @param {Object} inventoryData
   * @param {Object} variantsData
   * @param {boolean} isExtendedAssortment
   */
  function selectFirstAvailableSwatch(productCard, swatchInputs, inventoryData, variantsData, isExtendedAssortment) {
    if (!swatchInputs || swatchInputs.length === 0) return;

    // Check if currently selected swatch is already available (pre-selected by Liquid)
    var currentlyChecked = productCard.querySelector('.js-option:checked');
    if (currentlyChecked && !currentlyChecked.hasAttribute('hidden')) {
      var currentVariantId = currentlyChecked.dataset.variantId;
      var currentInventory = inventoryData[currentVariantId];
      var currentVariantData = variantsData[currentVariantId] || {};

      if (currentInventory) {
        var currentStatus = CustomInventoryAPI.getStockStatus({
          ownStock: currentInventory.ownStock || 0,
          externalStock: currentInventory.externalStock || 0,
          inventoryPolicy: currentVariantData.inventoryPolicy || 'deny',
          deliveryTime: currentVariantData.deliveryTime || '',
          isExtendedAssortment: isExtendedAssortment
        });

        // If current selection is available, don't change it (prevents flicker)
        if (currentStatus.isAvailable) {
          return;
        }
      }
    }

    // Find first available (visible, not hidden) swatch
    var firstAvailableSwatch = null;

    for (var i = 0; i < swatchInputs.length; i++) {
      var input = swatchInputs[i];
      // Skip hidden swatches
      if (input.hasAttribute('hidden')) continue;

      var variantId = input.dataset.variantId;
      var inventory = inventoryData[variantId];
      var variantData = variantsData[variantId] || {};
      var inventoryPolicy = variantData.inventoryPolicy || 'deny';
      var deliveryTime = variantData.deliveryTime || '';

      if (inventory) {
        var status = CustomInventoryAPI.getStockStatus({
          ownStock: inventory.ownStock || 0,
          externalStock: inventory.externalStock || 0,
          inventoryPolicy: inventoryPolicy,
          deliveryTime: deliveryTime,
          isExtendedAssortment: isExtendedAssortment
        });

        if (status.isAvailable) {
          firstAvailableSwatch = input;
          break;
        }
      }
    }

    // If no available swatch found, use first visible swatch
    if (!firstAvailableSwatch) {
      for (var j = 0; j < swatchInputs.length; j++) {
        if (!swatchInputs[j].hasAttribute('hidden')) {
          firstAvailableSwatch = swatchInputs[j];
          break;
        }
      }
    }

    // Only dispatch change if we're actually changing the selection
    if (firstAvailableSwatch && firstAvailableSwatch !== currentlyChecked) {
      firstAvailableSwatch.checked = true;
      firstAvailableSwatch.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Reorder swatches: in-stock first, then out-of-stock
   * Preserves original order within each group
   * @param {HTMLElement} productCard
   * @param {Object} inventoryData
   * @param {Object} variantsData
   * @param {boolean} isExtendedAssortment
   */
  function reorderSwatches(productCard, inventoryData, variantsData, isExtendedAssortment) {
    const swatchContainer = productCard.querySelector('.card__swatches');
    if (!swatchContainer) return;

    const swatchInputs = Array.from(swatchContainer.querySelectorAll('.js-option[data-variant-id]'));
    if (swatchInputs.length <= 1) return;

    // Count originally visible swatches (before reordering)
    const originalVisibleCount = swatchInputs.filter(input => !input.hasAttribute('hidden')).length;
    const swatchLimit = originalVisibleCount > 0 ? originalVisibleCount : swatchInputs.length;

    // Build swatch data with availability
    const swatchesWithAvailability = swatchInputs.map((input, originalIndex) => {
      const variantId = input.dataset.variantId;
      const inventory = inventoryData[variantId];
      const inventoryPolicy = variantsData[variantId]?.inventoryPolicy || 'deny';
      const deliveryTime = variantsData[variantId]?.deliveryTime || '';

      let isAvailable = false;
      if (inventory) {
        const status = CustomInventoryAPI.getStockStatus({
          ownStock: inventory.ownStock || 0,
          externalStock: inventory.externalStock || 0,
          inventoryPolicy,
          deliveryTime,
          isExtendedAssortment
        });
        isAvailable = status.isAvailable;
      }

      const label = swatchContainer.querySelector(`label[for="${input.id}"]`);

      return { input, label, isAvailable, originalIndex };
    });

    // Sort: available first (original order), then unavailable (original order)
    swatchesWithAvailability.sort((a, b) => {
      if (a.isAvailable === b.isAvailable) {
        return a.originalIndex - b.originalIndex;
      }
      return a.isAvailable ? -1 : 1;
    });

    // Get overflow count element if exists
    const countElement = swatchContainer.querySelector('.swatch-overflow-count');

    // Reorder DOM elements
    swatchesWithAvailability.forEach(({ input, label }, index) => {
      swatchContainer.appendChild(input);
      if (label) {
        swatchContainer.appendChild(label);
      }

      // Update hidden attribute based on swatch limit
      if (index >= swatchLimit) {
        input.setAttribute('hidden', '');
        if (label) label.setAttribute('hidden', '');
      } else {
        input.removeAttribute('hidden');
        if (label) label.removeAttribute('hidden');
      }
    });

    // Move +X element to end
    if (countElement) {
      swatchContainer.appendChild(countElement);
    }
  }

  /**
   * Fit swatches into one row using measured widths.
   * Uses ResizeObserver to re-measure when container width changes.
   * Works for all swatch types: color, image, size text.
   * @param {HTMLElement} container - .card__swatches--fitted element
   */
  function fitSwatches(container) {
    const counterEl = container.querySelector('.swatch-overflow-count');
    if (!counterEl) return;

    const labels = Array.from(container.querySelectorAll('.opt-label'));
    if (labels.length === 0) return;

    // Reset: show all labels (hidden via visibility to avoid flash), unhide inputs
    labels.forEach(label => {
      label.style.visibility = 'hidden';
      label.removeAttribute('hidden');
      const inputId = label.getAttribute('for');
      if (inputId) {
        const input = container.querySelector('#' + CSS.escape(inputId));
        if (input) input.removeAttribute('hidden');
      }
    });
    counterEl.setAttribute('hidden', '');
    container.classList.remove('js-fitted');

    const maxWidth = container.clientWidth;
    const counterReserve = 28;

    // Measure all label widths once (including margins)
    const widths = labels.map(label => {
      const style = getComputedStyle(label);
      return label.offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
    });

    // First pass: how many fit without "+N"?
    let usedWidth = 0;
    let fitCount = 0;
    for (let i = 0; i < widths.length; i++) {
      if (usedWidth + widths[i] <= maxWidth) {
        usedWidth += widths[i];
        fitCount = i + 1;
      } else {
        break;
      }
    }

    // If all fit, show all and done
    if (fitCount >= labels.length) {
      labels.forEach(label => { label.style.visibility = ''; });
      container.classList.add('js-fitted');
      return;
    }

    // Second pass: re-check with space reserved for "+N"
    usedWidth = 0;
    fitCount = 0;
    for (let i = 0; i < widths.length; i++) {
      if (usedWidth + widths[i] + counterReserve <= maxWidth || i === 0) {
        usedWidth += widths[i];
        fitCount = i + 1;
      } else {
        break;
      }
    }

    // Batch DOM update: hide overflow, show visible, update counter
    for (let i = 0; i < labels.length; i++) {
      labels[i].style.visibility = '';
      if (i >= fitCount) {
        labels[i].setAttribute('hidden', '');
        const inputId = labels[i].getAttribute('for');
        if (inputId) {
          const input = container.querySelector('#' + CSS.escape(inputId));
          if (input) input.setAttribute('hidden', '');
        }
      }
    }

    const hiddenCount = labels.length - fitCount;
    if (hiddenCount > 0) {
      counterEl.textContent = '+' + hiddenCount;
      counterEl.removeAttribute('hidden');
    }

    container.classList.add('js-fitted');
  }

  /**
   * Get or create shared ResizeObserver for swatch containers
   */
  function getResizeObserver() {
    if (!resizeObserver) {
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          fitSwatches(entry.target);
        }
      });
    }
    return resizeObserver;
  }

  /**
   * Process a single card using local DOM data
   * @param {HTMLElement} productCard - product-card element
   */
  function processCard(productCard) {
    if (processedCards.has(productCard)) return;
    processedCards.add(productCard);

    const inventoryDiv = productCard.querySelector('[data-product-id]');
    if (!inventoryDiv) return;

    const swatchInputs = productCard.querySelectorAll('.js-option[data-variant-id]');
    if (swatchInputs.length === 0) return;

    // Get inventory data from DOM JSON (no API call needed!)
    const inventoryData = getInventoryDataFromDOM(productCard);
    if (!inventoryData || Object.keys(inventoryData).length === 0) return;

    // Get variant metadata
    const variantsData = getVariantsData(productCard);

    // Get extended assortment status
    const isExtendedAssortment = inventoryDiv.dataset.isExtendedAssortment === 'true';

    // Size swatch products: skip reorder and selection, only mark unavailable
    const isSizeSwatchProduct = productCard.querySelector('.opt-label--size-text') !== null;

    if (!isSizeSwatchProduct) {
      // Reorder swatches: in-stock first
      reorderSwatches(productCard, inventoryData, variantsData, isExtendedAssortment);

      // Re-query swatchInputs after reordering (DOM has changed)
      const reorderedSwatchInputs = productCard.querySelectorAll('.js-option[data-variant-id]');

      // Select first available swatch and update image
      selectFirstAvailableSwatch(productCard, reorderedSwatchInputs, inventoryData, variantsData, isExtendedAssortment);
    }

    // Size swatch products: add click-to-navigate handler
    if (isSizeSwatchProduct) {
      const productLink = productCard.querySelector('a[data-product-url]');
      const baseUrl = productLink ? productLink.dataset.productUrl : null;
      if (baseUrl) {
        productCard.querySelectorAll('.opt-label--size-text').forEach((label) => {
          label.addEventListener('click', (e) => {
            e.preventDefault();
            const inputId = label.getAttribute('for');
            const input = productCard.querySelector('#' + CSS.escape(inputId));
            if (input) {
              const variantId = input.dataset.variantId;
              window.location.href = baseUrl + '?variant=' + variantId;
            }
          });
        });
      }
    }

    // Mark unavailable swatches
    const currentSwatchInputs = productCard.querySelectorAll('.js-option[data-variant-id]');
    currentSwatchInputs.forEach((input) => {
      const variantId = input.dataset.variantId;
      const inventory = inventoryData[variantId];

      const label = productCard.querySelector(`label[for="${input.id}"]`);
      if (!label) return;

      if (inventory) {
        const inventoryPolicy = variantsData[variantId]?.inventoryPolicy || 'deny';
        const deliveryTime = variantsData[variantId]?.deliveryTime || '';

        const status = CustomInventoryAPI.getStockStatus({
          ownStock: inventory.ownStock || 0,
          externalStock: inventory.externalStock || 0,
          inventoryPolicy,
          deliveryTime,
          isExtendedAssortment
        });

        if (!status.isAvailable) {
          label.classList.add('swatch--unavailable');
        } else {
          label.classList.remove('swatch--unavailable');
        }
      }
    });

    // Fit swatches to one row and observe container resizes
    const swatchContainer = productCard.querySelector('.card__swatches--fitted');
    if (swatchContainer) {
      fitSwatches(swatchContainer);
      getResizeObserver().observe(swatchContainer);
    }
  }

  /**
   * Process multiple cards
   * @param {HTMLElement[]} cards - Array of product-card elements
   */
  function processCards(cards) {
    if (!cards || cards.length === 0) return;

    cards.forEach(card => {
      if (!processedCards.has(card)) {
        processCard(card);
      }
    });
  }

  /**
   * Flush pending cards
   */
  function flushPendingCards() {
    if (pendingCards.length === 0) return;

    const cardsToProcess = pendingCards.slice();
    pendingCards = [];

    processCards(cardsToProcess);
  }

  /**
   * Add card to pending queue with debounce
   * @param {HTMLElement} card - product-card element
   */
  function queueCardForProcessing(card) {
    if (processedCards.has(card)) return;

    pendingCards.push(card);

    // Debounce: wait for more cards before processing
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(flushPendingCards, CONFIG.debounceDelay);
  }

  /**
   * Create IntersectionObserver for lazy loading
   */
  function createObserver() {
    return new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          queueCardForProcessing(entry.target);
          mainObserver.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '200px'
    });
  }

  /**
   * Observe new product cards
   * @param {NodeList|Array} cards - product-card elements
   */
  function observeCards(cards) {
    if (!mainObserver) {
      mainObserver = createObserver();
    }

    cards.forEach((card) => {
      if (!observedCards.has(card)) {
        observedCards.add(card);
        mainObserver.observe(card);
      }
    });
  }

  /**
   * Process visible cards immediately (for filtering scenarios)
   * @param {NodeList|Array} cards - product-card elements
   */
  function processCardsImmediately(cards) {
    const unprocessedCards = [];

    cards.forEach((card) => {
      if (!processedCards.has(card)) {
        unprocessedCards.push(card);
      }
    });

    if (unprocessedCards.length > 0) {
      processCards(unprocessedCards);
    }
  }

  /**
   * Initialize swatch availability for all product cards
   */
  async function init() {
    const apiAvailable = await waitForInventoryAPI();
    if (!apiAvailable) {
      console.warn('[CardSwatchStock] CustomInventoryAPI not available');
      return;
    }

    const productCards = document.querySelectorAll('product-card');
    if (productCards.length === 0) return;

    observeCards(productCards);
  }

  /**
   * Re-scan for new cards (used after filtering / load more)
   */
  function rescan() {
    const productCards = document.querySelectorAll('product-card');
    const unprocessedCards = [];

    productCards.forEach(card => {
      if (!processedCards.has(card)) {
        unprocessedCards.push(card);
      }
    });

    if (unprocessedCards.length > 0) {
      processCards(unprocessedCards);
    }

    observeCards(productCards);
  }

  // Initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Handle Shopify section renders (infinite scroll, etc.)
  document.addEventListener('shopify:section:load', (e) => {
    const cards = e.target.querySelectorAll('product-card');
    processCardsImmediately(cards);
    observeCards(cards);
  });

  // Handle faceted filtering (multiple possible events)
  ['facets:filter', 'facets:update', 'collection:filter'].forEach((eventName) => {
    document.addEventListener(eventName, () => {
      setTimeout(rescan, 150);
    });
  });

  // Handle History API navigation (filters via URL)
  window.addEventListener('popstate', () => {
    setTimeout(rescan, 150);
  });

  // Fallback: observe DOM changes in product grid and custom-pagination
  const initGridObserver = () => {
    const containers = document.querySelectorAll('custom-pagination, #filter-results, .main-products-grid__results');

    if (containers.length === 0) {
      return;
    }

    const gridObserver = new MutationObserver((mutations) => {
      const newCards = [];

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.matches?.('product-card')) {
              if (!processedCards.has(node)) {
                newCards.push(node);
              }
            }
            else if (node.querySelector?.('product-card')) {
              const cards = node.querySelectorAll('product-card');
              cards.forEach(card => {
                if (!processedCards.has(card)) {
                  newCards.push(card);
                }
              });
            }
          }
        });
      });

      if (newCards.length > 0) {
        setTimeout(() => processCards(newCards), 50);
      }
    });

    containers.forEach((container) => {
      gridObserver.observe(container, { childList: true, subtree: true });
    });
  };

  /**
   * Fallback: observe entire document for dynamically injected product cards
   * Catches cards from async loaders like Findify recommendations
   */
  function initBodyObserver() {
    const bodyObserver = new MutationObserver((mutations) => {
      const newCards = [];
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.('product-card') && !processedCards.has(node)) {
            newCards.push(node);
          } else if (node.querySelectorAll) {
            for (const card of node.querySelectorAll('product-card')) {
              if (!processedCards.has(card)) {
                newCards.push(card);
              }
            }
          }
        }
      }
      if (newCards.length > 0) {
        setTimeout(() => processCards(newCards), 50);
      }
    });

    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  // Start observers when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initGridObserver();
      initBodyObserver();
    });
  } else {
    initGridObserver();
    initBodyObserver();
  }

  // Handle bfcache restore - reset processed state and re-process cards
  document.addEventListener('on:bfcache:load-restore', () => {
    pendingCards = [];
    clearTimeout(debounceTimer);

    observedCards = new WeakSet();
    processedCards = new WeakSet();

    setTimeout(() => {
      const productCards = document.querySelectorAll('product-card');
      if (productCards.length > 0) {
        processCards(Array.from(productCards));
      }
    }, 100);
  });

})();
